import {
  canRecordBuilderResponse,
  canRecordFollowUp,
  canTransitionAction,
} from '../lib/actions';
import { supabase } from '../lib/supabase';
import type {
  ActionDraft,
  ActionEventRecord,
  ActionListItem,
  ActionRecord,
  ActionStatus,
} from '../types/actions';
import type { IssueRecord } from '../types/issues';

const ACTION_SELECT =
  'id, issue_id, requested_action, accountable_party, accountable_party_name, priority, due_date, follow_up_date, status, created_at, updated_at';

export async function loadActionForIssue(
  ownerId: string,
  issueId: string,
): Promise<ActionRecord | null> {
  const { data, error } = await supabase
    .from('actions')
    .select(ACTION_SELECT)
    .eq('owner_id', ownerId)
    .eq('issue_id', issueId)
    .maybeSingle();

  if (error) throw error;
  return (data as ActionRecord | null) ?? null;
}

export async function loadActionTimeline(
  ownerId: string,
  actionId: string,
): Promise<ActionEventRecord[]> {
  const { data, error } = await supabase
    .from('action_events')
    .select('id, action_id, event_type, from_status, to_status, note, created_at')
    .eq('owner_id', ownerId)
    .eq('action_id', actionId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data ?? []) as ActionEventRecord[];
}

export async function saveAction(
  ownerId: string,
  issue: IssueRecord,
  draft: ActionDraft,
  existingActionId?: string,
): Promise<ActionRecord> {
  const payload = {
    requested_action: draft.requestedAction.trim(),
    accountable_party: draft.accountableParty,
    accountable_party_name: draft.accountablePartyName.trim() || null,
    priority: draft.priority,
    due_date: draft.dueDate,
    follow_up_date: draft.followUpDate || null,
  };

  if (existingActionId) {
    const { data, error } = await supabase
      .from('actions')
      .update(payload)
      .eq('id', existingActionId)
      .eq('owner_id', ownerId)
      .select(ACTION_SELECT)
      .single();

    if (error) throw error;
    return data as ActionRecord;
  }

  const { data, error } = await supabase
    .from('actions')
    .insert({
      owner_id: ownerId,
      issue_id: issue.id,
      ...payload,
      status: 'draft',
    })
    .select(ACTION_SELECT)
    .single();

  if (error) throw error;

  const { error: issueError } = await supabase
    .from('issues')
    .update({ status: 'action_created' })
    .eq('id', issue.id)
    .eq('owner_id', ownerId);

  if (issueError) throw issueError;
  return data as ActionRecord;
}

function issueStatusForAction(status: ActionStatus) {
  switch (status) {
    case 'submitted':
      return 'submitted';
    case 'acknowledged':
    case 'in_progress':
    case 'disputed':
      return 'builder_responded';
    case 'rectification_claimed':
      return 'rectification_claimed';
    case 'verified_closed':
      return 'closed';
    case 'waived':
      return 'waived';
    default:
      return 'action_created';
  }
}

export async function transitionAction(
  ownerId: string,
  action: ActionRecord,
  nextStatus: ActionStatus,
): Promise<ActionRecord> {
  if (!canTransitionAction(action.status, nextStatus)) {
    throw new Error(`Action cannot move from ${action.status} to ${nextStatus}.`);
  }

  const { data, error } = await supabase
    .from('actions')
    .update({ status: nextStatus })
    .eq('id', action.id)
    .eq('owner_id', ownerId)
    .select(ACTION_SELECT)
    .single();

  if (error) throw error;

  const { error: issueError } = await supabase
    .from('issues')
    .update({ status: issueStatusForAction(nextStatus) })
    .eq('id', action.issue_id)
    .eq('owner_id', ownerId);

  if (issueError) throw issueError;
  return data as ActionRecord;
}

export async function recordBuilderResponse(
  ownerId: string,
  action: ActionRecord,
  note: string,
): Promise<ActionRecord> {
  if (!canRecordBuilderResponse(action.status)) {
    throw new Error('Mark the action as submitted before recording a builder response.');
  }

  const cleanNote = note.trim();
  if (cleanNote.length < 3) {
    throw new Error('Add a short builder response note before saving.');
  }

  let nextAction = action;
  if (action.status === 'submitted' || action.status === 'acknowledged') {
    const { data, error } = await supabase
      .from('actions')
      .update({ status: 'in_progress' })
      .eq('id', action.id)
      .eq('owner_id', ownerId)
      .select(ACTION_SELECT)
      .single();
    if (error) throw error;
    nextAction = data as ActionRecord;
  }

  const { error: eventError } = await supabase.from('action_events').insert({
    owner_id: ownerId,
    action_id: action.id,
    event_type: 'builder_response',
    from_status: action.status,
    to_status: nextAction.status,
    note: cleanNote,
  });
  if (eventError) throw eventError;

  const { error: issueError } = await supabase
    .from('issues')
    .update({ status: 'builder_responded' })
    .eq('id', action.issue_id)
    .eq('owner_id', ownerId);
  if (issueError) throw issueError;

  return nextAction;
}

export async function recordFollowUp(
  ownerId: string,
  action: ActionRecord,
  note: string,
): Promise<void> {
  if (!canRecordFollowUp(action.status)) {
    throw new Error('Mark the action as submitted before recording follow-up activity.');
  }

  const cleanNote = note.trim();
  if (cleanNote.length < 3) {
    throw new Error('Add a follow-up note before saving.');
  }

  const { error } = await supabase.from('action_events').insert({
    owner_id: ownerId,
    action_id: action.id,
    event_type: 'follow_up',
    from_status: action.status,
    to_status: action.status,
    note: cleanNote,
  });
  if (error) throw error;
}

export async function loadBuilderActionList(
  ownerId: string,
  assessmentId: string,
): Promise<ActionListItem[]> {
  const { data: issues, error: issueError } = await supabase
    .from('issues')
    .select('id, title, location')
    .eq('owner_id', ownerId)
    .eq('assessment_id', assessmentId);

  if (issueError) throw issueError;
  if (!issues?.length) return [];

  const issueById = new Map(issues.map((issue) => [issue.id, issue]));
  const { data: actions, error: actionError } = await supabase
    .from('actions')
    .select(ACTION_SELECT)
    .eq('owner_id', ownerId)
    .in('issue_id', issues.map((issue) => issue.id))
    .order('due_date', { ascending: true });

  if (actionError) throw actionError;

  return ((actions ?? []) as ActionRecord[]).map((action) => ({
    ...action,
    issue_title: issueById.get(action.issue_id)?.title ?? 'Finding',
    issue_location: issueById.get(action.issue_id)?.location ?? 'Location not recorded',
  }));
}
